import { createLoginToken, createLogoutToken } from './token.utils';
import { AuthStore } from '../../store/auth.state';
import { jwtDecode } from '../../utils/jwt.utils';
import { Injectable } from '@angular/core';
import { of, Subject } from 'rxjs';
import type { TokenService, TokenSignInOptions, TokenSignOutOptions } from './token.service';

@Injectable()
export class ChromeTokenService implements TokenService {

  constructor(
    private store: AuthStore
  ) { }

  signIn(options: TokenSignInOptions) {
    const result$ = new Subject<boolean>();

    const ssoToken = createLoginToken({
      appUrl: location.origin,
      queryForSpace: options.queryForSpace,
      returnUrl: chrome.identity.getRedirectURL()
    });

    chrome.identity.launchWebAuthFlow({ url: `${options.ssoUrl}/sso/${ssoToken}`, interactive: true }, responseUrl => {
      if (!responseUrl) {
        result$.error(new Error('responseUrl'));
        return;
      }

      const url = new URL(responseUrl);
      const token = url.searchParams.get('token');

      const result = jwtDecode(token);

      if (result && result.msg === 'LoginSuccessNotification') {
        this.store.dispatch('Auth_UpdateSsoResult', {
          token: result.ppl_token,
          spaceId: result.team_space_id
        });

        this.setToken(result.ppl_token);
        this.setAppSessionId(result.app_session_id);

        if (result.team_space_id) {
          localStorage.setItem('Pipeliner_Space', result.team_space_id);
        }

        result$.next(true);
      } else {
        result$.error(new Error('Token'));
      }
    });

    return result$.asObservable();
  }

  signOut(options: TokenSignOutOptions) {
    const result$ = new Subject<boolean>();

    const ssoToken = createLogoutToken({
      appSessionId: this.getAppSessionId(),
      appUrl: location.origin,
      returnUrl: chrome.identity.getRedirectURL()
    });

    chrome.identity.launchWebAuthFlow({ url: `${options.ssoUrl}/sso_logout/${ssoToken}` }, responseUrl => {
      if (!responseUrl) {
        result$.error(new Error('responseUrl'));
        return;
      }

      result$.next(true);
    });

    return result$.asObservable();
  }

  getToken() {
    try {
      const token = localStorage.getItem('Pipeliner_Token');

      if (token) {
        this.store.dispatch('Auth_UpdateSsoToken', token);
      }

      return of(token);
    } catch (ex) {
      return of(null);
    }
  }

  setToken(token: string) {
    try {
      localStorage.setItem('Pipeliner_Token', token);
    } catch (ex) { }

    return of(null);
  }

  deleteToken() {
    try {
      this.store.dispatch('Auth_UpdateSsoToken', null);

      localStorage.removeItem('Pipeliner_Token');
    } catch (ex) { }

    return of(null);
  }

  reloadPage() {
    window.location.href = 'index.html';
  }

  getAppSessionId() {
    try {
      return localStorage.getItem('Pipeliner_AppSessionId');
    } catch (ex) {
      return null;
    }
  }

  setAppSessionId(appSessionId: string) {
    try {
      localStorage.setItem('Pipeliner_AppSessionId', appSessionId);
    } catch (ex) { }

    return null;
  }

}
